import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingDown, Sparkles, Send, Building2, Users, ChevronDown, Share2, Mail, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Toast } from '../Toast';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Recommendation } from '../../data/mockRecommendationsData';
interface RecommendationDetailModalProps {
  recommendation: Recommendation | null;
  onClose: () => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}
export function RecommendationDetailModal({
  recommendation,
  onClose
}: RecommendationDetailModalProps) {
  useEscapeKey(onClose, !!recommendation);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (recommendation && messages.length === 0) {
      // Initial AI greeting
      setMessages([{
        id: '1',
        role: 'assistant',
        text: `I've identified that ${recommendation.rationale.toLowerCase()} This recommendation is expected to reduce risk by ${recommendation.riskReduction}%. What would you like to explore?`
      }]);
    }
  }, [recommendation]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!recommendation) return null;
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const quickPrompts = ['Why is this recommended?', 'What is the cost of inaction?', 'Draft an implementation plan', 'Who should own this?', 'Show me the source data'];
  const generateMockResponse = (userQuery: string): string => {
    const lowerQuery = userQuery.toLowerCase();
    if (lowerQuery.includes('source') || lowerQuery.includes('data') || lowerQuery.includes('evidence')) {
      const driverList = recommendation.linkedDrivers.map(d => `• **${d}**`).join('\n');
      return `This recommendation is based on the following source data:\n\n**Linked Risk Drivers:**\n${driverList}\n\n**Key observations:** ${recommendation.activityExamples?.length || 0} affected activities identified across ${recommendation.projectsAffected || recommendation.contractorsAffected || 1} ${recommendation.projectsAffected ? 'projects' : 'contractors'}.\n\n**Estimated risk reduction:** ${recommendation.riskReduction}% based on historical intervention effectiveness.\n\nWould you like me to drill into a specific driver or show the underlying observations?`;
    }
    if (lowerQuery.includes('why') || lowerQuery.includes('recommended')) {
      return `This recommendation targets ${recommendation.linkedDrivers.join(', ')}. ${recommendation.rationale} Based on historical data, addressing these drivers has consistently reduced incident rates.`;
    }
    if (lowerQuery.includes('cost') || lowerQuery.includes('inaction')) {
      return `Continuing without this intervention could maintain the current ${recommendation.riskReduction}% elevated risk exposure. Based on similar patterns across the portfolio, this translates to increased likelihood of incidents and potential project delays.`;
    }
    if (lowerQuery.includes('draft') || lowerQuery.includes('plan')) {
      return `Here is a draft implementation plan:\n\n**Phase 1 (Next 48h):**\n- Issue safety bulletin to affected ${recommendation.scope === 'project' ? 'project team' : 'teams'}\n- Conduct supervisor briefing on ${recommendation.linkedDrivers[0]}\n\n**Phase 2 (Next Week):**\n- Implement ${recommendation.title.toLowerCase()}\n- Monitor metrics for improvement`;
    }
    if (lowerQuery.includes('who') || lowerQuery.includes('own')) {
      return `Based on the scope (${recommendation.scope}) and affected ${recommendation.projectsAffected ? `${recommendation.projectsAffected} projects` : `${recommendation.contractorsAffected} contractors`}, this should be owned by ${recommendation.scope === 'systemic' ? 'Corporate Safety Leader' : recommendation.scope === 'region' ? 'Regional Safety Director' : 'Project Manager'} with support from field supervision.`;
    }
    return `Implementing "${recommendation.title}" is estimated to reduce risk by ${recommendation.riskReduction}% across the affected ${recommendation.scope === 'project' ? 'project' : 'projects'}. The primary drivers are ${recommendation.linkedDrivers.join(', ')}.`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: generateMockResponse(text)
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1500);
  };
  const handleActionClick = (action: string) => {
    setToastMessage(`${action} initiated`);
    setShowToast(true);
    setShowShareMenu(false);
  };
  if (!recommendation) return null;
  return <AnimatePresence data-id="element-1981" data-mp="hsa5rbo6f1l5">
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" data-id="element-1982" data-mp="huwh7me6u18n">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-1983" data-mp="dtb5439h9qzi" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1984" data-mp="3v5lo6wy3xs0p">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-1985" data-mp="2aq73quwaauoi">
            <div className="flex items-center gap-3" data-id="element-1986" data-mp="2fnz5egmqdv5w">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="element-1987" data-mp="1i1i7paa35fjm">
                <Sparkles size={20} className="text-purple-600" data-id="element-1988" data-mp="1hz5iqd5wu4x2" />
              </div>
              <div data-id="element-1989" data-mp="1n0cqwdbyt649">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-1990" data-mp="1mxqo0r00resd">
                  AI Recommendation
                </h2>
                <p className="text-sm text-gray-500" data-id="element-1991" data-mp="1wqegxanvoylt">
                  {recommendation.scope === 'systemic' ? 'Enterprise-wide intervention' : recommendation.scope === 'region' ? 'Regional intervention' : 'Project-specific action'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1992" data-mp="3tti27wsgizzc">
              <div className="relative" ref={shareMenuRef} data-id="element-1993" data-mp="3otvqjrsa1rsn">
                <button onClick={() => setShowShareMenu(!showShareMenu)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1994" data-mp="6mpjqprspy7o">
                  <Share2 size={16} data-id="element-1995" data-mp="bm2493v72t73" />
                  Take Action
                  <ChevronDown size={14} data-id="element-1996" data-mp="blscbaqlabyr" />
                </button>

                <AnimatePresence data-id="element-1997" data-mp="pdp4fes9clv5">
                  {showShareMenu && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10" data-id="element-1998" data-mp="lc3tw0374jo4">
                      <button onClick={() => handleActionClick('Drafting email')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-1999" data-mp="13zmbufs4fjg0">
                        <Mail size={16} className="text-gray-400" data-id="element-2000" data-mp="zz2jdpiht407" />
                        Draft email to team
                      </button>
                      <button onClick={() => handleActionClick('Creating action item')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-2001" data-mp="lb7pi5sr0tlj">
                        <CheckCircle2 size={16} className="text-gray-400" data-id="element-2002" data-mp="gco01vey4zof" />
                        Create action item
                      </button>
                      <button onClick={() => handleActionClick('Generating report')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-2003" data-mp="1z0mpvmyd6z44">
                        <FileText size={16} className="text-gray-400" data-id="element-2004" data-mp="1p406l2losg47" />
                        Generate implementation report
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-2005" data-mp="23yoaicbfv6hp">
                <X size={20} data-id="element-2006" data-mp="2vpkmlapod73x" />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden" data-id="element-2007" data-mp="30hvim2hao8r7">
            {/* Left Column: Recommendation Details */}
            <div className="w-full md:w-1/2 border-r border-gray-200 overflow-y-auto p-6 bg-white" data-id="element-2008" data-mp="p0sc857v0es8">
              <div className="mb-6" data-id="element-2009" data-mp="k2qtxmi9cjht">
                <div className="flex items-center gap-2 mb-3" data-id="element-2010" data-mp="ys6hyuqematt">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full border uppercase tracking-wider ${getPriorityColor(recommendation.priority)}`} data-id="element-2011" data-mp="yrmwx55gj6bk">
                    {recommendation.priority === 'critical' && <AlertTriangle size={10} data-id="element-2012" data-mp="15z4evc4h85b" />}
                    {recommendation.priority}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-600" data-id="element-2013" data-mp="b2lq1mc9w38e">
                    <Sparkles size={10} data-id="element-2014" data-mp="1riybfkg34ldg" />
                    AI
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-2015" data-mp="1mk3hpngkxcek">
                  {recommendation.title}
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2016" data-mp="1yj0jeu36r9mp">
                  <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-100" data-id="element-2017" data-mp="23j4yqgscxybc">
                    <p className="text-xs font-semibold mb-1 uppercase tracking-wider text-emerald-800" data-id="element-2018" data-mp="23hab2uppbs3k">
                      Risk Reduction
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-2019" data-mp="1aw0s67dm9bxg">
                      <span className="text-3xl font-bold text-emerald-600" data-id="element-2020" data-mp="1fsr72rdoq1ha">
                        -{recommendation.riskReduction}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2021" data-mp="1pkf2f3dd42o1">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider" data-id="element-2022" data-mp="s4rgpskacd38">
                      Scope
                    </p>
                    <div className="flex items-center gap-2" data-id="element-2023" data-mp="x0yc4rf2266x">
                      <span className="text-lg font-bold text-gray-900 capitalize" data-id="element-2024" data-mp="3ac163bsibyuw">
                        {recommendation.scope}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-6" data-id="element-2025" data-mp="31equfpbs27pg">
                  <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-2026" data-mp="2we2si8m0g6gg">
                    Rationale
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2027" data-mp="2rfi18yoqkx45">
                    {recommendation.rationale}
                  </p>
                </div>

                <div className="flex items-center gap-6 mb-6 py-4 border-y border-gray-100" data-id="element-2028" data-mp="2ilwotm01nrxw">
                  {recommendation.projectsAffected && <div className="flex items-center gap-2" data-id="element-2029" data-mp="2domtjppz4m25">
                      <Building2 size={18} className="text-gray-400" data-id="element-2030" data-mp="28oibvwdbxlac" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2031" data-mp="23ovwbga13syd">
                        {recommendation.projectsAffected} Projects
                      </span>
                    </div>}
                  {recommendation.contractorsAffected && <div className="flex items-center gap-2" data-id="element-2032" data-mp="ck2immgc47bu">
                      <Users size={18} className="text-gray-400" data-id="element-2033" data-mp="3u0akg0nmqxwe" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2034" data-mp="2ur9iztg72ny">
                        {recommendation.contractorsAffected} Contractors
                      </span>
                    </div>}
                </div>

                <div className="mb-6" data-id="element-2035" data-mp="3q0i617bk1xj4">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2036" data-mp="3pxdxjxr24fmn">
                    Expected Impact
                  </h4>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-2037" data-mp="3g113uzvii1rw">
                    <p className="text-sm text-amber-900 leading-relaxed" data-id="element-2038" data-mp="19bcsma2gk1id">
                      {recommendation.expectedImpact}
                    </p>
                  </div>
                </div>

                <div className="mb-6" data-id="element-2039" data-mp="2yrytt0oej8dd">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2040" data-mp="2yt07lcwsy8ys">
                    Linked Risk Drivers
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2041" data-mp="2trasnm6glb5f">
                    {recommendation.linkedDrivers.map(driver => <span key={driver} className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-lg" data-id="element-2042" data-mp="33qtbu0ejoswf">
                        {driver}
                      </span>)}
                  </div>
                </div>

                {recommendation.activityExamples && recommendation.activityExamples.length > 0 && <div data-id="element-2043" data-mp="3mfzytzmdg6o1">
                      <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-2044" data-mp="1x2cxw3ypld89">
                        Affected Activities
                      </h4>
                      <div className="space-y-2" data-id="element-2045" data-mp="1s5cuk0t8uoku">
                        {recommendation.activityExamples.map((ex, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-2046" data-mp="3s8dvun2qu2o9" data-mpi={idx}>
                            <div className="flex items-center gap-2 mb-1" data-id="element-2047" data-mp="53fzuyt0512s">
                              <span className="text-sm font-bold text-gray-900" data-id="element-2048" data-mp="a1zmz2jc0iwq">
                                {ex.activityName}
                              </span>
                              {recommendation.scope !== 'project' && <span className="text-[10px] font-medium text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-2049" data-mp="opavswo4pot5">
                                  {ex.projectName}
                                </span>}
                            </div>
                            <p className="text-xs text-gray-600" data-id="element-2050" data-mp="12p3k6lc6nsxy">
                              {ex.explanation}
                            </p>
                          </div>)}
                      </div>
                    </div>}
              </div>
            </div>

            {/* Right Column: AI Chat */}
            <div className="w-full md:w-1/2 flex flex-col bg-gray-50" data-id="element-2051" data-mp="12vmcyujjhjaq">
              <div className="p-4 border-b border-gray-200 bg-white shadow-sm z-10" data-id="element-2052" data-mp="ytr9pki1svub">
                <div className="flex items-center gap-2" data-id="element-2053" data-mp="1vj8oqq2x7u8o">
                  <Sparkles size={18} className="text-purple-600" data-id="element-2054" data-mp="1viqjj3ya65om" />
                  <h3 className="font-bold text-gray-900" data-id="element-2055" data-mp="1qlgmp38karqr">
                    Discuss with Bilton AI
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2056" data-mp="1lrceyhrh2fom">
                  Ask questions to explore this recommendation in depth.
                </p>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2057" data-mp="2ebuf4uv26slv">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-2058" data-mp="2t7lhiymcnly2">
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-purple-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-2059" data-mp="2y04upuh4kf2c">
                      {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-purple-600" data-id="element-2060" data-mp="3gww8jodwe1z0">
                          <Sparkles size={12} data-id="element-2061" data-mp="3gujtpk3fs7q6" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2062" data-mp="31ylshnenod73">
                            Bilton AI
                          </span>
                        </div>}
                      <div className="whitespace-pre-wrap leading-relaxed" data-id="element-2063" data-mp="1qjp1w92tpfxm">
                        {msg.text}
                      </div>
                    </div>
                  </div>)}

                {isTyping && <div className="flex justify-start" data-id="element-2064" data-mp="dnrhxefyovk7">
                    <div className="bg-white border border-gray-200 p-4 rounded-2xl rounded-tl-sm shadow-sm flex gap-1" data-id="element-2065" data-mp="8m24xt96i7th">
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" data-id="element-2066" data-mp="mltmu52tg8cr" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-2067" data-mp="iljamaofklpg" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-2068" data-mp="3nihtpiumpzj" />
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-2069" data-mp="33bs5fyv84ltj" />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-gray-200" data-id="element-2070" data-mp="1xckulg8l8zdm">
                {/* Quick Prompts */}
                <div className="flex flex-wrap gap-2 mb-3" data-id="element-2071" data-mp="1tcadb7fcimjg">
                  {quickPrompts.map((prompt, idx) => <button key={idx} onClick={() => handleSend(prompt)} className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium rounded-full transition-colors whitespace-nowrap" data-id="element-2072" data-mp="273nsk9680y8o" data-mpi={idx}>
                      {prompt}
                    </button>)}
                </div>

                <div className="relative" data-id="element-2073" data-mp="2q187ooj6oig3">
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Ask about this recommendation..." className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow" data-id="element-2074" data-mp="2l2nhlhxnbf5d" />
                  <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:hover:bg-purple-600 transition-colors" data-id="element-2075" data-mp="2tjxkpgexxhk2">
                    <Send size={16} data-id="element-2076" data-mp="2olw2expc9m9n" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-2077" data-mp="2engbauqbad05" />
    </AnimatePresence>;
}